import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import PageMeta from "@/components/PageMeta";

const TEAM_SIZES = ["1-10", "11-50", "51-200", "201-1000", "1000+"];
const WORKSHOP_TYPES = ["Leadership offsite", "Sales team", "All-hands wellness", "Ongoing program", "Other"];
const CHALLENGES = ["Burnout / stress", "Sales pressure", "Focus & productivity", "Team resilience", "Sleep & recovery", "Other"];

export default function Corporate() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company_name: "", contact_person: "", email: "", phone: "",
    team_size: TEAM_SIZES[1], main_challenge: CHALLENGES[0], workshop_type: WORKSHOP_TYPES[0], message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/corporate-enquiries", form);
      setDone(true);
      toast.success("Enquiry submitted.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
          <CheckCircle2 className="h-12 w-12 mx-auto text-primary" />
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-4">We'll be in touch.</h1>
          <p className="text-muted-foreground mt-3 max-w-md mx-auto">Thanks for reaching out. A team member will follow up within one business day.</p>
          <Button className="mt-8 rounded-full" onClick={() => navigate("/")} data-testid="corp-home-btn">Back to home</Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-12 lg:py-16">
      <PageMeta
        title="Team Workshops & Programs"
        description="Build a calmer, sharper, more resilient team. Breathwork workshops for leadership teams, sales floors, and high-pressure operating units."
      />
      <p className="overline text-primary mb-2">Corporate</p>
      <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight">Team workshops & programs</h1>
      <p className="text-muted-foreground mt-2 max-w-xl">Build a calmer, sharper, more resilient team. Tell us about your group.</p>

      <Card className="mt-8 sm:mt-10 p-5 sm:p-8">
        <form onSubmit={onSubmit} className="space-y-6" data-testid="corporate-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Company name" id="c-company"><Input id="c-company" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="corp-company-input" /></Field>
            <Field label="Contact person" id="c-person"><Input id="c-person" required value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="corp-person-input" /></Field>
            <Field label="Email" id="c-email"><Input id="c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="corp-email-input" /></Field>
            <Field label="Phone" id="c-phone"><Input id="c-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="corp-phone-input" /></Field>
            <Field label="Team size" id="c-size">
              <Select value={form.team_size} onValueChange={(v) => setForm({ ...form, team_size: v })}>
                <SelectTrigger id="c-size" data-testid="corp-size-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TEAM_SIZES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Workshop type" id="c-wt">
              <Select value={form.workshop_type} onValueChange={(v) => setForm({ ...form, workshop_type: v })}>
                <SelectTrigger id="c-wt" data-testid="corp-wt-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WORKSHOP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Main challenge" id="c-ch">
            <Select value={form.main_challenge} onValueChange={(v) => setForm({ ...form, main_challenge: v })}>
              <SelectTrigger id="c-ch" data-testid="corp-challenge-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CHALLENGES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Message" id="c-msg">
            <Textarea id="c-msg" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us anything that would help us serve your team better." data-testid="corp-message-input" />
          </Field>
          <div className="flex justify-end">
            <Button type="submit" size="lg" disabled={submitting} className="w-full sm:w-auto rounded-full" data-testid="corp-submit-btn">
              {submitting ? "Submitting..." : "Submit enquiry"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function Field({ label, id, children }) {
  return <div className="space-y-2"><Label htmlFor={id}>{label}</Label>{children}</div>;
}
